import Big from 'big.js'
import yn from 'yn'
import dayjs from 'dayjs'
import Cookies from 'js-cookie'
import { set } from 'lodash'

import reduceConfiguration from './reducers/reduceConfiguration'
import reduceCrossingAvailability from './reducers/reduceCrossingAvailability'
import reduceAttractions from './reducers/reduceAttractions'
import reducePostCartResponse from './reducers/reducePostCartResponse'
import reduceAccommodations, {
  formatHotelResources,
  reduceMultiRoomSearch,
} from './reducers/reduceAccommodations'
import reduceReserveCartResponse from './reducers/reduceReserveCartResponse'
import reduceBookingViewResponse from './reducers/reduceBookingViewResponse'
import reduceItineraries from './reducers/reduceItineraries'
import formatUser from '../utilities/user/formatUser'
import getDefaultModifyCustomerAccountValues from '../utilities/user/getDefaultModifyCustomerAccountValues'
import UserRoles from '../utilities/UserRoles'
import { generateGuarantees } from './utilities'
import { openWindowWithPost } from '../utilities/cartUtilities'
import getErrorMessage from '../utilities/getErrorMessage'
import { validateEmail } from '../utilities/validateEmail'

export const SESSION_STORAGE_SESSION_ID = 'rfSessionIId'

const generateSessionId = () =>
  Math.random().toString(36).substring(2, 15) +
  Math.random().toString(36).substring(2, 15)

class RedFunnelAPI {
  constructor(axios, config) {
    this.axios = axios
    const axiosInstance = axios.create({
      params: {
        clientId: config.clientId,
        sessionId: generateSessionId(),
      },
    })
    axiosInstance.interceptors.request.use(localConfig => {
      if (localConfig?.url?.split('/ferry/')?.[1]) {
        // any endpoint matching this will keep the session alive
        this.lastSessionRefresh = new Date().getTime()
      }
      return localConfig
    })
    this.axiosInstance = axiosInstance
    this.config = config
    this.endpoints = this.setupEndpoints(config)
    this.lastSessionRefresh = new Date().getTime()
  }

  getLastSessionHit() {
    return this.lastSessionRefresh
  }

  setupEndpoints(config) {
    const {
      dolliEndpoint,
      // drupalEndpoint
    } = config
    return {
      configuration: `${dolliEndpoint}/config?`,
      crossings: `${dolliEndpoint}/ferry/crossings/available-best-prices`,
      customer: `${dolliEndpoint}/ferry/customer`,
      customers: `${dolliEndpoint}/ferry/customers`,
      coupon: `${dolliEndpoint}/ferry/coupon`,
      login: `${dolliEndpoint}/ferry/customer/login`,
      newSessions: `${dolliEndpoint}/ferry/active-new-session`,
      activeSession: `${dolliEndpoint}/ferry/active-session/user`,
      passwordReset: `${dolliEndpoint}/ferry/password-resets`,
      attractions: `${dolliEndpoint}/ferry/attractions`,
      travelCardPrices: `${dolliEndpoint}/ferry/travel-card-prices`,
      travelCards: `${dolliEndpoint}/ferry/travel-cards`,
      accommodations: `${dolliEndpoint}/hotel/hotel-stay/redfunnel/calendar`,
      availableRooms: `${dolliEndpoint}/hotel/avail-rooms`,
      cart: `${dolliEndpoint}/itinerary-services/carts`,
      reserveCart: `${dolliEndpoint}/itinerary-services/reserve`,
      ferryVehicles: `${dolliEndpoint}/content/feeds/ferryVehicles`,
      ferryTravelCards: `${dolliEndpoint}/content/feeds/ferryTravelCards`,
      agentUserLogin: `${dolliEndpoint}/ferry/agent-user/login`,
      agentAccountLogin: `${dolliEndpoint}/ferry/agent-accounts/login`,
      callCentreLogin: `${dolliEndpoint}/ferry/api-logins`,
      agentUser: `${dolliEndpoint}/ferry/agent-user`,
      accountAgent: `${dolliEndpoint}/ferry/agent-accounts`,
      booking: `${dolliEndpoint}/itinerary-services/itineraries`,
      ferryFares: `${dolliEndpoint}/content/feeds/ferryFares`,
      vessels: `${dolliEndpoint}/content/feeds/ferryVessels`,
      ferrySettings: `${dolliEndpoint}/content/feeds/ferrySettings`,
      ferrySundries: `${dolliEndpoint}/content/feeds/ferrySundries`,
      paymentCards: `${dolliEndpoint}/ferry/payment-cards`,
      itinerary: `${dolliEndpoint}/itinerary-services/itineraries`,
      missingItineraries: `${dolliEndpoint}/itinerary-services/itineraries/create-missing-from-bookit`, // https://dev-redfunnel.dolli.cloud/api/itinerary-services/itineraries/create-missing-from-bookit
      hotelContent: `${dolliEndpoint}/content/property`,
      loyaltyAccounts: `${dolliEndpoint}/ferry/loyalty-accounts`,
      invoices: `${dolliEndpoint}/payment/invoices`,
      invoice: `${dolliEndpoint}/ferry/invoices`,
      invoicePreload: `${dolliEndpoint}/payment/preload`,
      invoicePayment: `${dolliEndpoint}/payment/invoices`,
      resendEmail: `${dolliEndpoint}/itinerary-services/transactional-emails`,
      resendEticket: `${dolliEndpoint}/ferry/e-tickets`,
      ferryBase: `${dolliEndpoint}/ferry`,
      paymentToken: `${dolliEndpoint}/redfunnel/payment-token`,
    }
  }

  /**
   * API Methods
   * - - - - - - - -
   */

  getConfiguration(primaryLanguageId) {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .get(endpoints.configuration, {
        params: {
          primaryLanguageId,
        },
      })
      .then(res => {
        const data = reduceConfiguration(res)
        this.configuration = data
        return { data, raw: res }
      })
  }

  getActiveNewSessions() {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .get(endpoints.newSessions)
      .then(res => res?.data?.['hydra:member']?.[0]?.sessionData?.sessionCount)
  }

  validateSession(userId, sessionId) {
    const { axiosInstance, endpoints } = this
    return axiosInstance.get(`${endpoints.activeSession}/${userId}`, {
      params: {
        sessionId,
      },
    })
  }

  startNewSession() {
    this.axiosInstance.defaults.params.sessionId = generateSessionId()
    this.getActiveNewSessions()
  }

  getSessionID() {
    return this.axiosInstance.defaults.params.sessionId
  }

  setSessionId(id) {
    this.axiosInstance.defaults.params.sessionId = id
  }

  getBookingConfirmationURL(itineraryId, sessionId) {
    return `${process.env.REACT_APP_SUMMARY_URL}?itineraryId=${itineraryId}&sessionId=${sessionId}`
  }

  // FERRIES
  async getCrossings(params, rejectOnPromoError = false) {
    const { axiosInstance, endpoints, configuration } = this
    let couponResourceCode
    if (params?.couponCode) {
      try {
        const couponRes = await axiosInstance.get(
          `${endpoints.coupon}/${params.couponCode}?departureDate=${params.departureDate}&returnDate=${params.returnDate}`,
        )
        const matchingCoupon = couponRes?.data?.coupons?.find(
          coupon => coupon.couponNumber === params.couponCode,
        )
        if (!matchingCoupon?.couponValid) {
          throw new Error('Coupon not valid for searched dates.')
        }
        couponResourceCode = matchingCoupon?.productCode
      } catch (err) {
        couponResourceCode = ''
        if (rejectOnPromoError) {
          throw new Error(getErrorMessage(err, 'Invalid coupon code'))
        }
      }
    }

    return axiosInstance
      .get(endpoints.crossings, {
        params: {
          ...params,
          // only pass couponCode if it was valid
          couponCode:
            couponResourceCode && params?.couponCode ? params?.couponCode : '',
          productCode: params?.productCode, // ticket RED200-1154
        },
      })
      .then(res => {
        return {
          data: {
            ...reduceCrossingAvailability(
              res,
              configuration,
              params,
              couponResourceCode,
            ),
            promoData: {
              searchedPromo: params?.couponCode,
              hasValidPromo: !!couponResourceCode,
            },
          },
        }
      })
  }

  // ATTRACTIONS
  getAttractions({ startDate }) {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .get(endpoints.attractions, {
        // the api will only return results if startDate and endDate are the same but requires both.
        params: {
          startDate,
          endDate: startDate,
          currency: 'GBP',
        },
      })
      .then(res => ({
        raw: res,
        data: reduceAttractions(res?.data?.['hydra:member'] || []),
      }))
  }

  getAccommodations({
    startDate,
    endDate,
    hotelCode,
    exactMatchOnly,
    language,
    currency,
    hotelProvider,
    rooms = [
      {
        adults: 1,
        children: 0,
      },
    ],
  }) {
    const { axiosInstance, endpoints } = this

    const promises = rooms.map(room =>
      axiosInstance.get(endpoints.accommodations, {
        params: {
          startDate,
          endDate,
          hotelCode,
          adults: room.adults ?? 1,
          children: room.children ?? 0,
          exactMatchOnly: exactMatchOnly ?? 'False',
          primaryLanguageId: language ?? 'en',
          currency: currency ?? 'GBP',
          hotelProvider: hotelProvider ?? 3,
        },
      }),
    )

    return Promise.all(promises).then(responses => {
      return {
        data: reduceMultiRoomSearch(responses, rooms),
      }
    })
  }

  getAccommodationsContent() {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .get(endpoints.hotelContent)
      .then(res => reduceAccommodations(res?.data?.['hydra:member'] || []))
  }

  getAvailableRooms(
    {
      startDate,
      endDate,
      exactMatchOnly,
      language,
      currency,
      hotelProvider,
      hotelCode,
      rooms = [
        {
          adults: 1,
          children: 0,
        },
      ],
    },
    signal = null,
  ) {
    const { axiosInstance, endpoints } = this

    const promises = rooms.map(({ adults, children }) =>
      axiosInstance.get(endpoints.availableRooms, {
        params: {
          startDate,
          endDate,
          adults: adults ?? 1,
          children: children ?? 0,
          exactMatchOnly: exactMatchOnly ?? 'False',
          primaryLanguageId: language ?? 'en',
          currency: currency ?? 'GBP',
          hotelProvider: hotelProvider ?? 3,
          hotelCode,
        },
        signal,
      }),
    )
    return Promise.all(promises).then(responses => ({
      data: responses.map(response =>
        formatHotelResources(
          response?.data?.['hydra:member']?.[0]?.roomStays?.[0]?.roomTypes,
        ),
      ),
    }))
  }

  // Travel cards
  getTravelCardPrices({ priceDate }) {
    const { axiosInstance, endpoints } = this
    return axiosInstance.get(endpoints.travelCardPrices, {
      params: { priceDate },
    })
  }

  postTravelCard({ params }) {
    const { axiosInstance, endpoints } = this
    return axiosInstance.post(endpoints.travelCards, {
      params,
    })
  }

  // USERS
  registerCustomer(params) {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .post(endpoints.customer, {
        createParameters: [params],
      })
      .then(res => {
        // store our user
        const user = res?.data?.['hydra:member']?.[0]
        if (user) {
          const storageId = axiosInstance?.defaults?.params?.sessionId
          sessionStorage.setItem(SESSION_STORAGE_SESSION_ID, storageId)
          sessionStorage.setItem(storageId, JSON.stringify(user))
        }
        return user
      })
  }

  registerLoyaltyAccount(customerNumber) {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .post(endpoints.loyaltyAccounts, { customerNumber })
      .then(res => {
        return res?.data?.['hydra:member']?.[0]
      })
  }

  modifyCustomer(customerId, params) {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .put(`${endpoints.customer}/${customerId}/modify`, {
        modifyParameters: [params],
      })
      .then(res => res?.data?.['hydra:member']?.[0])
  }

  modifyAgentUser(customerId, params) {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .put(`${endpoints.agentUser}/${customerId}/modify`, {
        modifyParameters: [params],
      })
      .then(res => res?.data?.['hydra:member']?.[0])
  }

  modifyAccountAgent(customerId, params) {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .patch(`${endpoints.accountAgent}/${customerId}`, {
        ...params,
      })
      .then(res => res?.data?.['hydra:member']?.[0])
  }

  updateCustomerPassword(user, newPassword, currentPassword) {
    return this.modifyCustomer(user.customerNumber, {
      password: newPassword,
      currentPassword,
    })
  }

  updateAgentUserPassword(user, newPassword, currentPassword) {
    if (user.role === UserRoles.ACCOUNT_AGENT) {
      return this.modifyAccountAgent(user.customerNumber, {
        email: user?.email,
        password: newPassword,
        currentPassword,
      })
    }

    return this.modifyAgentUser(user.customerNumber, {
      password: newPassword,
      currentPassword,
      email: user?.email,
      name: user?.agentUser?.name,
      initials: user?.agentUser?.initials,
      departmentCode: user?.agentUser?.departmentCode,
      workPhoneNumber: user?.agentUser?.workPhoneNumber,
      homePhoneNumber: user?.agentUser?.homePhoneNumber,
      mobilePhoneNumber: user?.agentUser?.mobilePhoneNumber,
    })
  }

  updateAgentDetails(user, params) {
    if (user.role === UserRoles.ACCOUNT_AGENT) {
      return this.modifyAccountAgent(user.customerNumber, params)
    }
    throw new Error('This feature is not implemented for this user type')
    // this currently requires the password be sent for every request,
    // and entering the wrong pw just updates it.
    // return this.modifyAgentUser(user.customerNumber, params)
  }

  storeSessionUser(user) {
    const storageId = this.axiosInstance?.defaults?.params?.sessionId
    sessionStorage.setItem(storageId, JSON.stringify(user))
  }

  updateCustomerAccount(user, updates) {
    const params = getDefaultModifyCustomerAccountValues({
      ...(user?.customerAccount || {}),
      ...updates,
    })

    return this.modifyCustomer(user.customerNumber, params).then(
      updatedUser => {
        const formattedUser = formatUser({
          ...updatedUser,
          customerAccount: params,
        })
        this.storeSessionUser(formattedUser)
        return formattedUser
      },
    )
  }

  handleLoginRes(res) {
    const { axiosInstance } = this
    const user = res?.data?.['hydra:member']?.[0]

    if (user) {
      if (res.ccId) {
        user.ccId = res.ccId
      }
      const storageId = axiosInstance?.defaults?.params?.sessionId
      sessionStorage.setItem(SESSION_STORAGE_SESSION_ID, storageId)
      sessionStorage.setItem(storageId, JSON.stringify(user))
      return user
    }
    return null
  }

  universalLogin(params) {
    return this.login(params).catch(() => {
      return this.agentLogin(params).catch(() => {
        return this.agentUserLogin({
          agentNumber: params.id,
          password: params.password,
        })
      })
    })
  }

  login(params) {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .post(endpoints.login, {
        loginParameters: params,
      })
      .then(res => this.handleLoginRes(res))
  }

  agentLogin(params) {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .post(endpoints.agentUserLogin, { loginParameters: params })
      .then(res => this.handleLoginRes(res))
  }

  agentUserLogin(params) {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .post(endpoints.agentAccountLogin, { ...params })
      .then(res => {
        return this.handleLoginRes(res)
      })
  }

  callCentreLogin(params) {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .post(endpoints.callCentreLogin, { ...params })
      .then(res =>
        this.handleLoginRes({
          ...res,
          ccId: params.username,
        }),
      )
  }

  async logout(user) {
    const { axiosInstance, endpoints } = this
    const base =
      user.role === 'agentUser' ? endpoints.agentUser : endpoints.customer
    const endPoint = `${base}/${user.customerNumber}/logout`
    let res
    try {
      res = await axiosInstance.get(endPoint)
    } catch (err) {
      // keep going anyway
      res = null
    }
    const storageId = this.axiosInstance.defaults.params.sessionId
    sessionStorage.removeItem(storageId)
    sessionStorage.removeItem(SESSION_STORAGE_SESSION_ID)
    await this.startNewSession()
    return res
  }

  /**
   * @param {String} id // email or id
   * */
  passwordResetRequest(id) {
    const isEmail = !!validateEmail(id)
    const { axiosInstance, endpoints } = this
    // we now allow this from multiple places and therefor don't know the user type
    return axiosInstance
      .post(endpoints.passwordReset, {
        email: isEmail ? id : undefined,
        customerNumber: isEmail ? undefined : id,
        accountType: 'customer',
        passwordResetUrl: `${process.env.REACT_APP_MY_ACCOUNT_URL}/update-password/`,
      })
      .catch(() => {
        return axiosInstance
          .post(endpoints.passwordReset, {
            email: isEmail ? id : undefined,
            agentNumber: isEmail ? undefined : id,
            accountType: 'agent',
            passwordResetUrl: `${process.env.REACT_APP_AGENT_PORTAL_URL}/update-password/agent/`,
          })
          .catch(() => {
            return axiosInstance.post(endpoints.passwordReset, {
              email: isEmail ? id : undefined,
              agentNumber: isEmail ? undefined : id,
              accountType: 'agent-user',
              passwordResetUrl: `${process.env.REACT_APP_AGENT_PORTAL_URL}/update-password/agent-user`,
            })
          })
      })
  }

  /**
   * @param {String} resetToken
   * @param {Object} params
   * @param {String} params.password
   * @param {String} params.accountType
   * */
  passwordReset(resetToken, params) {
    const { axiosInstance, endpoints } = this
    return axiosInstance.put(`${endpoints.passwordReset}/${resetToken}`, params)
  }

  /* User's travel cards */
  getTravelCards(customerNumber) {
    const { axiosInstance, endpoints } = this
    return axiosInstance.get(
      `${endpoints.customers}/${customerNumber}/travel-cards`,
    )
  }

  getFerryTravelCards() {
    const { axiosInstance, endpoints } = this
    return axiosInstance.get(endpoints.ferryTravelCards)
  }

  getUserVehicles(customerNumber) {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .get(`${endpoints.customers}/${customerNumber}/co-vehicles`)
      .then(res => {
        const vehicles = res?.data?.coVehicles || []
        return vehicles.map(vehicle => ({
          ...vehicle,
          isDefault: yn(vehicle.isDefault),
        }))
      })
      .catch(err => {
        const message =
          err?.response?.data?.['hydra:description'] ||
          err?.message ||
          'Unable to fetch user vehicles'
        throw new Error(message)
      })
  }

  updateUserVehicle({ customerNumber, vehicles }) {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .put(`${endpoints.customers}/${customerNumber}/co-vehicles`, {
        coVehicles: vehicles,
      })
      .then(res => {
        const coVehicles = res?.data?.['hydra:member']?.[0]?.coVehicles || []
        return coVehicles.map(vehicle => ({
          ...vehicle,
          isDefault: yn(vehicle.isDefault),
        }))
      })
  }

  getFerryVehicles() {
    const { axiosInstance, endpoints } = this
    return axiosInstance.get(`${endpoints.ferryVehicles}`).then(res => {
      const data = res?.data?.data || []
      return data.map(item => ({
        ...item,
        isDefault: yn(item.isDefault),
      }))
    })
  }

  postCart(params) {
    const { axiosInstance, endpoints } = this

    return axiosInstance
      .post(endpoints.cart, {
        ...params,
      })
      .then(res => {
        const data = res?.data?.['hydra:member']?.[0]
        return {
          raw: data,
          data: reducePostCartResponse(data, this.configuration),
        }
      })
  }

  reserveCartPrePayment({ cartId, allowPaymentPlans }) {
    const { axiosInstance, endpoints } = this
    const sessionId = this.getSessionID()
    const queryParams = {
      sessionId,
    }

    const reqBody = {
      cartId,
    }
    if (allowPaymentPlans) {
      reqBody.guaranteesAccepted = [
        { guaranteeTypeCode: 41, providerName: 'BookIt' },
      ]
    }
    return axiosInstance
      .post(endpoints.reserveCart, reqBody, {
        params: queryParams,
      })
      .then(res => {
        const data = res?.data?.['hydra:member']?.[0]
        return {
          raw: data,
          data: {
            ...reducePostCartResponse(data, this.configuration),
            itineraryId: data?.id,
          },
        }
      })
  }

  /**
   * @param {String} cartId
   * @param {Object} [paymentData={}]
   * @param {Object} [options={}]
   * @param {String} [options.successRedirect=process.env.REACT_APP_SUMMARY_URL] without query params
   * @param {String} [options.failRedirect=process.env.REACT_APP_SUMMARY_URL] without query params
   */
  reserveCart({ cartId, paymentData = {}, options = {} }) {
    const sessionId = this.getSessionID()
    const queryParams = {
      sessionId,
    }
    const postData = {
      ...paymentData,
      cartId,
      guaranteesAccepted: generateGuarantees({ paymentData }),
    }
    const successRedirect =
      options?.successRedirect || process.env.REACT_APP_SUMMARY_URL
    const failRedirect =
      options?.failRedirect || process.env.REACT_APP_SUMMARY_URL
    const { axiosInstance, endpoints } = this

    return axiosInstance
      .post(
        endpoints.reserveCart,
        {
          ...postData,
        },
        {
          params: queryParams,
        },
      )
      .then(res => {
        const data = res?.data?.['hydra:member']?.[0]
        return {
          raw: data,
          data: reduceReserveCartResponse({
            data,
            sessionId,
            customerNumber: paymentData?.customerNumber || null,
            successRedirect,
            failRedirect,
            paymentData,
          }),
          itineraryId: data?.id,
        }
      })
  }

  getPaymentToken(tokenId, sessionId, email) {
    const { axiosInstance, endpoints } = this

    return axiosInstance
      .get(`${endpoints.paymentToken}/${tokenId}`, {
        params: { sessionId },
        headers: email ? { Email: email } : {},
      })
      .then(res => {
        const data = res?.data?.payload
        if (!data) throw new Error('Empty token payload')
        return data
      })
      .catch(err => {
        throw new Error(getErrorMessage(err, 'Unable to retrieve token'))
      })
  }

  savePaymentCard(body, sessionId) {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .post(endpoints.paymentCards, body, {
        params: { sessionId },
        headers: { 'Content-Type': 'application/json' },
      })
      .then(res => res?.data?.['hydra:member']?.[0])
      .catch(err => {
        throw new Error(getErrorMessage(err, 'Unable to save card'))
      })
  }

  processPayment({ itineraryId, cartId, sessionId, providerData, tokenMeta }) {
    const { axiosInstance, endpoints } = this
    const url = `${endpoints.booking}/${itineraryId}/payment`
    const awcToken = tokenMeta.awcToken || Cookies.get('awc')
    const guaranteesAccepted = [
      {
        guaranteeTypeCode: 46,
        providerName: 'ekashu',
        paymentInfo: {
          providerData,
          ...(awcToken ? { awinToken: awcToken } : {}),
        },
      },
    ]
    const { guaranteeTypeCode, redemptionQuantity, travelCardPin, email } =
      tokenMeta
    if (guaranteeTypeCode && redemptionQuantity) {
      const extra = {
        guaranteeTypeCode: Number(guaranteeTypeCode),
        loyaltyRedemption: { redemptionQuantity: Number(redemptionQuantity) },
      }
      if (travelCardPin) extra.paymentCards = [{ seriesCode: travelCardPin }]
      guaranteesAccepted.push(extra)
    }
    const body = { guaranteesAccepted, ...(cartId ? { cartId } : {}) }

    return axiosInstance
      .put(url, body, {
        params: { sessionId },
        headers: email
          ? { 'Content-Type': 'application/json', Email: email }
          : { 'Content-Type': 'application/json' },
      })
      .then(res => res?.data?.['hydra:member']?.[0])
      .catch(err => {
        throw new Error(getErrorMessage(err, 'Payment failed'))
      })
  }

  itineraryPayment({
    itineraryId,
    paymentData = {},
    options = {},
    isModify = false,
    cartId = null,
  }) {
    const sessionId = this.getSessionID()

    const successRedirect =
      options?.successRedirect || process.env.REACT_APP_SUMMARY_URL
    const failRedirect =
      options?.failRedirect || process.env.REACT_APP_SUMMARY_URL

    const guaranteesAccepted = generateGuarantees({ paymentData })

    const calculateOnlyGuarantee = guaranteesAccepted?.find(
      guarantee => !!guarantee.calculateOnly,
    )
    const { axiosInstance, endpoints } = this

    const extraHeaders = paymentData?.email
      ? {
          Email: paymentData?.email,
        }
      : {}

    const awcToken = Cookies.get('awc')
    if (
      awcToken &&
      guaranteesAccepted?.length &&
      paymentData.paymentCardOption !== 'new-card'
    ) {
      set(guaranteesAccepted, [0, 'paymentInfo', 'awinToken'], awcToken)
    }
    const reqBody = {
      ...paymentData,
      guaranteesAccepted,
    }
    if (cartId) {
      reqBody.cartId = cartId
    }

    return axiosInstance
      .put(`${endpoints.itinerary}/${itineraryId}/payment`, reqBody, {
        params: {
          sessionId,
        },
        headers: {
          ...extraHeaders,
        },
      })
      .then(async res => {
        let data = res?.data?.['hydra:member']?.[0]
        const isUsingMultiplePayments = guaranteesAccepted?.length > 1
        if (
          calculateOnlyGuarantee &&
          paymentData?.paymentCardOption === 'saved-card'
        ) {
          // check if first one failed.
          const errorMessage = data?.guaranteesAccepted?.find(
            guarantee => !!guarantee.error,
          )?.error

          if (errorMessage) {
            throw new Error(errorMessage)
          }

          try {
            const calculateOnlyGuaranteeResponse = await axiosInstance.put(
              `${endpoints.itinerary}/${itineraryId}/payment`,
              {
                ...paymentData,
                guaranteesAccepted: [
                  {
                    ...calculateOnlyGuarantee,
                    calculateOnly: false,
                  },
                ],
              },
              {
                params: {
                  sessionId,
                },
                headers: {
                  ...extraHeaders,
                },
              },
            )
            data = calculateOnlyGuaranteeResponse?.data?.['hydra:member']?.[0]
          } catch (err) {
            const message = getErrorMessage(err, 'failed to make payment.')
            throw new Error(message)
          }
        }

        const paymentErrors = data?.guaranteesAccepted
          ?.map(guarantee => guarantee?.error)
          .filter(err => !!err)

        if (paymentErrors?.length) {
          const errorMessage = isUsingMultiplePayments
            ? 'Your payment method has been declined, please contact our contact centre at +44 (0)23 8001 9192 or try again later'
            : paymentErrors[0]

          throw new Error(errorMessage)
        }

        const reducedData = reduceReserveCartResponse({
          data,
          sessionId,
          customerNumber: paymentData?.customerNumber || null,
          agentNumber: paymentData?.agentNumber || null,
          successRedirect,
          failRedirect,
          paymentData,
          email: paymentData?.email,
          guaranteeTypeCode: calculateOnlyGuarantee?.guaranteeTypeCode || null,
          redemptionQuantity:
            calculateOnlyGuarantee?.loyaltyRedemption?.redemptionQuantity ||
            null,
          isModify,
          cartId,
        })
        const requiresPreload = guaranteesAccepted?.some(
          guarentee => guarentee?.providerName === 'preloadEkashu',
        )

        if (requiresPreload && !reducedData?.ekashu) {
          throw new Error('No preload data returned.')
        }

        return {
          raw: data,
          data: { ...reducedData },
          itineraryId,
        }
      })
  }

  getBooking(bookingId, hasPaymentFailure = false, anonymousUserEmail = null) {
    const { axiosInstance, endpoints } = this
    const params = {}
    if (hasPaymentFailure) {
      params.paymentFailed = true
    }

    const additionalHeaders = anonymousUserEmail
      ? {
          Email: anonymousUserEmail,
        }
      : {}

    return axiosInstance
      .get(`${endpoints.booking}/${bookingId}`, {
        params,
        headers: {
          ...additionalHeaders,
        },
      })
      .then(res => {
        const data = res?.data
        return {
          raw: data,
          data: reduceBookingViewResponse(data, this.configuration),
        }
      })
      .catch(err => {
        return { error: err }
      })
  }

  // sms subscriptions
  getSubscriptions(customerNumber) {
    const { axiosInstance, endpoints } = this
    return axiosInstance.get(
      `${endpoints.customers}/${customerNumber}/subscriptions`,
    )
  }

  modifySubscriptions(customerNumber, params) {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .put(`${endpoints.customers}/${customerNumber}/subscriptions`, params)
      .then(res => {
        return res?.data?.['hydra:member']?.[0]
      })
  }

  getFerryFares() {
    const { axiosInstance, endpoints } = this
    return axiosInstance.get(`${endpoints.ferryFares}`)
  }

  getVessels() {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .get(`${endpoints.vessels}`)
      .then(res => res?.data?.data || [])
  }

  getFerrySettings() {
    const { axiosInstance, endpoints } = this
    return axiosInstance.get(`${endpoints.ferrySettings}`)
  }

  getAvailableSundries() {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .get(`${endpoints.ferrySundries}`)
      .then(res => res?.data?.data || [])
  }

  getLoyaltyPoints(customerNumber) {
    const { axiosInstance, endpoints } = this
    return axiosInstance.get(
      `${endpoints.customers}/${customerNumber}/loyalty-account`,
    )
  }

  getPaymentCards(customerNumber) {
    const { axiosInstance, endpoints } = this
    return axiosInstance.get(endpoints.paymentCards, {
      params: {
        customerNumber,
      },
    })
  }

  getAgentPaymentCards(agentNumber) {
    const { axiosInstance, endpoints } = this
    return axiosInstance.get(endpoints.paymentCards, {
      params: {
        agentNumber,
      },
    })
  }

  postPaymentCard({ params }) {
    const { axiosInstance, endpoints } = this
    return axiosInstance.post(endpoints.paymentCards, {
      params,
    })
  }

  deletePaymentCard(cardId, customerNumber) {
    const { axiosInstance, endpoints } = this
    return axiosInstance.delete(`${endpoints.paymentCards}/${cardId}`, {
      params: { customerNumber },
    })
  }

  getItineraries({ pageNumber }) {
    const { axiosInstance, endpoints } = this
    const params = new URLSearchParams()
    if (pageNumber) {
      params.append('page', pageNumber)
    }
    return axiosInstance
      .get(`${endpoints.itinerary}?${params.toString()}`)
      .then(res => {
        const raw = res?.data?.['hydra:member']
        return {
          raw,
          data: reduceItineraries(raw),
        }
      })
      .catch(err => {
        throw (
          err?.response?.['hydra:description'] ||
          err?.message ||
          'Failed to fetch itineraries'
        )
      })
  }

  getItinerary(itineraryId) {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .get(`${endpoints.itinerary}/${itineraryId}`)
      .then(res => {
        const raw = res?.data
        const formatted = reduceBookingViewResponse(raw, this.configuration)
        return {
          raw,
          data: {
            id: itineraryId,
            ...(formatted || {}),
          },
        }
      })
  }

  // https://dev-redfunnel.dolli.cloud/api/itinerary-services/itineraries/create-missing-from-bookit
  getUnResolvedItineraries() {
    const { axiosInstance, endpoints } = this

    return axiosInstance
      .post(
        endpoints.missingItineraries,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      .then(res => res?.data?.['hydra:member']?.[0]?.missingBookingReferences)
  }

  // /api/ferry/booking/{{id}}/cancel endpoint takes true/false for the commitChanges
  getCancellationFees(itineraryId) {
    const { axiosInstance, endpoints } = this

    return axiosInstance
      .put(`${endpoints.ferryBase}/booking/${itineraryId}/cancel`, {
        modifyParameters: [
          {
            commitChanges: false,
            id: itineraryId,
          },
        ],
      })
      .then(res => res?.data?.['hydra:member']?.[0] || null)
  }

  cancelItinerary(itineraryId) {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .put(`${endpoints.itinerary}/${itineraryId}/cancel`, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then(res => res?.data)
  }

  // /api/ferry/booking/11632952/modify?
  putFerryItem({ itineraryId, params }) {
    const { axiosInstance, endpoints } = this
    return axiosInstance
      .put(`${endpoints.ferryBase}/booking/${itineraryId}/modify`, {
        modifyParameters: [params],
      })
      .then(res => res?.data?.['hydra:member']?.[0] || null)
  }

  getAgentInvoices({ agentId, startDate, endDate, referenceNumbers = null }) {
    const { axiosInstance, endpoints } = this
    const params = {
      agentId,
      provider: 3,
      searchFromDate: startDate,
      searchToDate: endDate,
    }

    if (referenceNumbers?.length) {
      params.referenceNumbers = referenceNumbers.join(',')
    }

    return axiosInstance
      .get(endpoints.invoices, { params })
      .then(res => res?.data?.['hydra:member'] || [])
  }

  getAgentInvoice({ invoiceId }) {
    const { axiosInstance, endpoints } = this
    return axiosInstance.get(endpoints.invoice, {
      params: {
        invoiceId,
      },
    })
  }

  fetchInvoicePreloadData({ totalCost, orderId }) {
    const { axiosInstance, endpoints } = this

    return axiosInstance.post(
      endpoints.invoicePreload,
      {
        createParameters: {
          txnTotal: totalCost,
          orderId,
        },
      },
      {
        params: {
          providerId: 0,
        },
      },
    )
  }

  async payInvoices({ invoices, paymentData, agentNumber }) {
    const { axiosInstance, endpoints } = this
    const orderId = `${agentNumber}-${dayjs.tz().format('YYYYMMDDHHmm')}`
    const guaranteesAccepted = generateGuarantees({ paymentData })

    let preloadData
    const sessionId = this.getSessionID()
    const totalCost = invoices.reduce((num, { amount }) => {
      const asBig = new Big(+num)
      return asBig.add(+amount).toFixed(2)
    }, 0)

    const returnUrlSearchParams = new URLSearchParams()

    returnUrlSearchParams.append('sessionId', sessionId)

    const formattedInvoices = invoices.map(invoice => ({
      ...invoice,
      amount: Number(invoice?.amount || 0),
    }))

    returnUrlSearchParams.append('invoices', JSON.stringify(formattedInvoices))
    returnUrlSearchParams.append('totalCost', totalCost)
    const returnUrl = `${
      window.location.origin
    }/agent-portal/invoices/confirmation?${returnUrlSearchParams.toString()}`

    const requiresPreload = guaranteesAccepted?.some(
      guarantee => guarantee.providerName === 'preloadEkashu',
    )

    if (requiresPreload) {
      const preloadRes = await this.fetchInvoicePreloadData({
        totalCost,
        orderId,
      })
      preloadData = preloadRes?.data?.['hydra:member']?.[0]?.preload?.data
      if (!preloadData) {
        throw new Error('Unable to get preload data')
      }

      // expects an array of {key: 'someKey', value: 'someValue'}
      const data = Object.entries(preloadData).map(([key, value]) => ({
        key,
        value,
      }))
      data.push({
        key: 'orderId',
        value: orderId,
      })
      data.push({
        key: 'invoices',
        value: JSON.stringify(formattedInvoices),
      })
      data.push({
        key: 'isInvoicePayment',
        value: true,
      })
      data.push({ key: 'sessionId', value: sessionId })
      data.push({
        key: 'agentNumber',
        value: agentNumber,
      })
      data.push({
        key: 'redirectUrl',
        value: returnUrl,
      })
      data.push({
        key: 'redirectFailUrl',
        value: `${returnUrl}&paymentFailed=true`,
      })
      data.push({
        key: 'reference_id',
        value: `${agentNumber}-${dayjs.tz().format('YYYYMMDDHHmm')}`,
      })
      try {
        openWindowWithPost(process.env.REACT_APP_EKASHU_URL, data)
      } catch (err) {
        throw new Error(err)
      }
      return null
    }

    return axiosInstance
      .post(
        endpoints.invoicePayment,
        {
          agentId: agentNumber,
          currencyCode: 'GBP',
          orderId,
          guaranteesAccepted: preloadData
            ? guaranteesAccepted.map(guarantee => {
                if (guarantee.providerName === 'ekashu') {
                  return {
                    ...guarantee,
                    paymentInfo: {
                      providerData: preloadData,
                    },
                  }
                }

                return guarantee
              })
            : guaranteesAccepted,
          invoices,
        },
        {
          params: {
            provider: 3,
          },
        },
      )
      .then(res => {
        const data = res?.data?.['hydra:member']?.[0]
        return {
          raw: data,
          data: reduceReserveCartResponse({
            data,
            sessionId: this.getSessionID(),
            customerNumber: agentNumber,
            successRedirect: `${window.location.origin}/agent-portal/invoices/confirmation`,
            failRedirect: `${window.location.origin}/agent-portal/invoices/confirmation`,
            paymentData,
          }),
        }
      })
  }

  resendConfirmation(itineraryId) {
    const { axiosInstance, endpoints } = this
    return axiosInstance.post(endpoints.resendEmail, {
      itineraryId,
      type: 'confirmation',
    })
  }

  resendEticket(bookingNumber, emailAddress, priced) {
    const { axiosInstance, endpoints } = this
    return axiosInstance.post(endpoints.resendEticket, {
      bookingNumber,
      emailAddress,
      priced,
    })
  }
}

export { RedFunnelAPI }
